// Live provisioning progress for the phone Assign flow. Opened right after POST /api/phones/assign
// succeeds; polls GET /api/phones/:mac/provision-status every 2s and renders the real backend
// stage (queued -> pushing -> pushed -> registered | error) as a checklist so the operator sees
// "we found the phone, we're pushing config, it's rebooting, it registered" instead of a frozen UI.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, X, Loader2, AlertTriangle, Phone, CircleDashed, ChevronDown, ChevronRight } from 'lucide-react';
import { getToken } from '../lib/api';
import { Dialog } from './Dialog';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

type Status = {
  assigned: boolean;
  stage: 'unassigned' | 'queued' | 'pushing' | 'pushed' | 'registered' | 'error';
  extension?: string | null;
  realm?: string | null;
  ip?: string | null;
  via_sbc?: boolean;
  registered?: boolean;
  error_message?: string | null;
  elapsed_seconds?: number | null;
  push_result?: string | null;
  reason?: string | null;
  hint?: string | null;
  where?: 'freeswitch' | 'sbc' | 'server' | 'server->phone' | null;
  assigned_at?: string | null;
  push_at?: string | null;
};

// Human label for the layer that last handled (or stalled) the provision — answers "where did it hang".
const WHERE_LABEL: Record<string, string> = {
  freeswitch: 'FreeSWITCH (registered)',
  sbc: 'On-site SBC (LAN push to phone)',
  server: 'Stratus server',
  'server->phone': 'Stratus server → phone (direct LAN push)',
};

// How long before we hint that a reboot is just taking a while (not stuck).
const SLOW_AFTER_SEC = 120;
// How long before we STOP the spinner and call the registration step stalled — a
// phone that got its config reboots + registers in 30-60s, so past this it is not
// "still working", it is not coming back on its own (SBC tunnel down, phone on a
// different VLAN, wrong network). The step turns amber with an actionable hint
// instead of spinning forever (which read as "success is imminent").
const REGISTER_DEADLINE_SEC = 150;

type StepState = 'done' | 'active' | 'pending' | 'error' | 'warn';
function StepRow({ state, title, detail }: { state: StepState; title: string; detail?: string }) {
  const icon =
    state === 'done' ? <Check size={16} className="text-success" />
    : state === 'error' ? <X size={16} className="text-danger" />
    : state === 'warn' ? <AlertTriangle size={16} className="text-warning" />
    : state === 'active' ? <Loader2 size={16} className="animate-spin text-primary" />
    : <CircleDashed size={16} className="text-textSecondary/50" />;
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className={`text-sm ${state === 'pending' ? 'text-textSecondary' : ''}`}>{title}</div>
        {detail && <div className="text-xs text-textSecondary mt-0.5 break-words">{detail}</div>}
      </div>
    </div>
  );
}

export function ProvisionProgressModal({ mac, label, onClose }: { mac: string; label?: string; onClose: () => void; }) {
  const macKey = (mac || '').replace(/[^a-fA-F0-9]/g, '').toLowerCase();
  const [openedAt] = useState(() => Date.now());
  const [showDetails, setShowDetails] = useState(false);
  const [, force] = useState(0);
  // tick once a second so the elapsed timer / slow hint update between polls
  useEffect(() => { const t = setInterval(() => force(n => n + 1), 1000); return () => clearInterval(t); }, []);

  const status = useQuery<Status>({
    queryKey: ['provision-status', macKey],
    queryFn: async () => {
      const r = await fetch(`/api/phones/${macKey}/provision-status`, { headers: auth().headers });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    // Stop polling once the phone is registered (terminal success); keep polling otherwise so a
    // late SBC push / reboot can still flip it to registered. Guard: never terminate on a
    // 'registered' that belongs to a PRIOR assignment (see isStale below) — re-assigning an
    // already-registered phone would otherwise show an instant false "Done" and stop polling.
    refetchInterval: (q) => {
      const d = q.state.data;
      const fresh = d?.push_at ? new Date(d.push_at).getTime() >= openedAt - 5000
                  : d?.assigned_at ? new Date(d.assigned_at).getTime() >= openedAt - 5000 : true;
      return (d?.stage === 'registered' && fresh) ? false : 2000;
    },
    refetchOnWindowFocus: false,
  });

  const sRaw = status.data;
  // #685: the modal opens at CLICK time, so the first poll(s) can return the state of a PREVIOUS
  // assignment of this same MAC (re-assign is a supported flow — assigned rows stay in the list).
  // Treat any status whose timestamps predate this modal's open as "not yet assigned" so we never
  // render a stale "registered"/"stalled" for the old assignment. 5s slack absorbs clock skew.
  const isStale = (() => {
    if (!sRaw) return false;
    const ts = sRaw.push_at ? new Date(sRaw.push_at).getTime()
             : sRaw.assigned_at ? new Date(sRaw.assigned_at).getTime() : null;
    return ts != null && ts < openedAt - 5000;
  })();
  const s = isStale ? undefined : sRaw;
  const stage = isStale ? 'unassigned' : (s?.stage ?? 'queued');
  const viaSbc = !!s?.via_sbc;
  const elapsed = Math.floor((Date.now() - openedAt) / 1000);
  // Prefer the server's own elapsed (measured from the push, survives modal reopen)
  // over the wall-clock since this modal opened.
  const sincePush = s?.elapsed_seconds ?? elapsed;
  const slow = elapsed > SLOW_AFTER_SEC && stage !== 'registered' && stage !== 'error';
  // Config was delivered (pushed) but the phone still hasn't registered well past
  // the reboot window → treat as stalled, not "still working".
  const regStalled = stage === 'pushed' && sincePush > REGISTER_DEADLINE_SEC;

  // The real delivery path, so the modal never claims a mechanism that isn't happening
  // (#685: it said "Contacting <ip>" while the phone actually provisioned via PnP pull).
  const isPnp = s?.push_result === 'pnp_pull' || s?.push_result === 'pnp_notify_sent';
  const notifySent = s?.push_result === 'pnp_notify_sent';

  // Map the backend stage onto a 3-step checklist. 'unassigned' = the modal opened at
  // click time (#685) and the assign endpoint hasn't committed yet — step 1 is live.
  const steps = useMemo<{ title: string; detail?: string; state: StepState }[]>(() => {
    const assigning = stage === 'unassigned';
    const pushActive = stage === 'queued' || stage === 'pushing';
    const pushDone = stage === 'pushed' || stage === 'registered';
    const pushErr = stage === 'error';
    return [
      {
        title: assigning ? 'Assigning extension' : `Assigned to extension ${s?.extension ?? ''}`.trim(),
        detail: assigning ? 'Writing device records and publishing the configuration.'
          : (s?.realm ? `Registrar realm ${s.realm}` : undefined),
        state: assigning ? 'active' : 'done',
      },
      {
        title: viaSbc ? 'Sending configuration to the on-site SBC'
          : isPnp ? 'Publishing config and notifying the phone (SIP PnP)'
          : 'Pushing configuration to the phone',
        detail: pushErr
          ? (s?.error_message || 'Push failed')
          : viaSbc
            ? 'The SBC picks up the config on its next check-in and pushes it over the local network.'
            : isPnp
              ? (notifySent
                  ? `Config published and the phone at ${s?.ip || 'its address'} was notified — it should fetch and register shortly.`
                  : 'Config published; the phone pulls it over multicast PnP on its next announce or reboot.')
              : (s?.ip ? `Contacting ${s.ip} and applying settings` : undefined),
        state: pushErr ? 'error' : pushDone ? 'done' : pushActive ? 'active' : 'pending',
      },
      {
        title: isPnp ? 'Phone fetches config and registers' : 'Phone reboots and registers',
        detail: stage === 'registered'
          ? 'Registered and reachable.'
          : regStalled
            ? `No registration after ${sincePush}s — the phone has not come back online.`
            : pushDone
              ? (notifySent
                  ? 'Waiting for the phone to apply its config and register (usually well under a minute).'
                  : 'Waiting for the phone to reboot and come back online (usually 30–60s).')
              : undefined,
        state: stage === 'registered' ? 'done' : regStalled ? 'warn' : pushDone ? 'active' : 'pending',
      },
    ];
  }, [stage, viaSbc, isPnp, notifySent, s?.extension, s?.realm, s?.ip, s?.error_message, regStalled, sincePush]);

  const done = stage === 'registered';
  const errored = stage === 'error';

  const footer = (
    <button className="btn-primary" onClick={onClose}>{done ? 'Done' : 'Close'}</button>
  );

  return (
    <Dialog open onClose={onClose} title="Provisioning phone" size="sm" footer={footer}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-success/15' : errored ? 'bg-danger/15' : regStalled ? 'bg-warning/15' : 'bg-primary/15'}`}>
          {done ? <Check size={18} className="text-success" />
            : errored ? <AlertTriangle size={18} className="text-danger" />
            : regStalled ? <AlertTriangle size={18} className="text-warning" />
            : <Phone size={18} className="text-primary" />}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{label || `Phone ${macKey}`}</div>
          <div className="text-xs text-textSecondary font-mono">{macKey}</div>
        </div>
      </div>

      <div className="border border-border rounded-md px-3 py-1 bg-bg/40 divide-y divide-border/60">
        {steps.map((st, i) => <StepRow key={i} {...st} />)}
      </div>

      {done && (
        <div className="mt-3 text-sm text-success flex items-center gap-2">
          <Check size={16} /> Phone is online and ready to make calls.
        </div>
      )}

      {errored && (
        <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 p-3">
          <div className="text-sm text-danger flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span className="font-medium">{s?.reason || 'Provisioning failed.'}</span>
          </div>
          {s?.where && (
            <div className="text-xs text-textSecondary mt-2">
              <span className="opacity-70">Hung up at:</span> {WHERE_LABEL[s.where] || s.where}
            </div>
          )}
          {s?.hint && <div className="text-xs text-textSecondary mt-1"><span className="opacity-70">Next step:</span> {s.hint}</div>}
        </div>
      )}

      {/* Config delivered but the phone never registered — stop pretending it's imminent and
          surface the concrete next step (SBC tunnel vs direct-LAN differ). */}
      {!done && !errored && regStalled && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/10 p-3">
          <div className="text-sm text-warning flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span className="font-medium">Config was delivered, but the phone hasn’t registered.</span>
          </div>
          <div className="text-xs text-textSecondary mt-2">
            {viaSbc
              ? (s?.hint || 'The on-site SBC received the config but no registration has reached the PBX. Open the SBC’s logs (Stratus SBC → Open logs) and check its tunnel to the server.')
              : (s?.hint || 'The phone got its config but never came back to register. Check it finished rebooting and can reach the PBX on its network.')}
          </div>
        </div>
      )}
      {!done && !errored && !regStalled && slow && (
        <div className="mt-3 text-sm text-textSecondary flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />
          <span>{s?.reason || `Still working after ${elapsed}s — the phone may still be rebooting.`}{' '}
            {s?.hint || 'You can leave this open, or close it and watch the Phones list.'}</span>
        </div>
      )}
      {!done && !errored && !regStalled && !slow && (
        <div className="mt-3 text-xs text-textSecondary">{s?.reason || `Working… ${elapsed}s elapsed.`} You can keep working — this updates automatically.</div>
      )}

      {/* Technical details — always available for debugging (what the backend actually observed). */}
      <button
        type="button"
        onClick={() => setShowDetails(v => !v)}
        className="mt-3 text-xs text-textSecondary hover:text-primary inline-flex items-center gap-1"
      >
        {showDetails ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Technical details
      </button>
      {showDetails && (
        <dl className="mt-2 text-xs border border-border rounded-md bg-bg/40 divide-y divide-border/60">
          {([
            ['MAC', macKey],
            ['Extension', s?.extension || '—'],
            ['Registrar realm', s?.realm || '—'],
            ['Phone IP', s?.ip || '—'],
            ['Path', s?.via_sbc ? 'Via on-site SBC' : isPnp ? 'Local SIP PnP (pull)' : 'Direct LAN push'],
            ['Stage', stage],
            ['Where', s?.where ? (WHERE_LABEL[s.where] || s.where) : '—'],
            ['Push result', s?.push_result || '—'],
            ['Server elapsed', s?.elapsed_seconds != null ? `${s.elapsed_seconds}s` : '—'],
          ] as [string, string][]).map(([k, v]) => (
            <div key={k} className="flex gap-3 px-3 py-1.5">
              <dt className="text-textSecondary w-32 shrink-0">{k}</dt>
              <dd className="font-mono break-all">{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </Dialog>
  );
}

export default ProvisionProgressModal;
